import { RouteObject } from 'react-router-dom';
import { lazy } from 'react';

// Lazy load components
const HomePage = lazy(() => import('../pages/home/page'));
const HomePageEN = lazy(() => import('../pages/en/home/page'));
const AboutPage = lazy(() => import('../pages/about/page'));
const AboutOverviewPage = lazy(() => import('../pages/about/overview/page'));
const AboutMissionPage = lazy(() => import('../pages/about/mission/page'));
const AboutHistoryPage = lazy(() => import('../pages/about/history/page'));
const AboutOrganizationPage = lazy(() => import('../pages/about/organization/page'));
const AboutActivitiesPage = lazy(() => import('../pages/about/activities/page'));
const AboutAccessPage = lazy(() => import('../pages/about/access/page'));

// Services
const ButlerServicesPage = lazy(() => import('../pages/butler-services/page'));
const FootmanServicePage = lazy(() => import('../pages/services/footman/page'));
const ButlerServicePage = lazy(() => import('../pages/services/butler/page'));
const SecondStewardServicePage = lazy(() => import('../pages/services/second-steward/page'));
const HouseStewardServicePage = lazy(() => import('../pages/services/house-steward/page'));

// Qualifications pages
const QualificationsPage = lazy(() => import('../pages/qualifications/page'));
const AssociateButlerPage = lazy(() => import('../pages/qualifications/associate-butler/page'));
const ProfessionalButlerPage = lazy(() => import('../pages/qualifications/professional-butler/page'));
const MasterButlerPage = lazy(() => import('../pages/qualifications/master-butler/page'));
const BusinessMannerPage = lazy(() => import('../pages/qualifications/business-manner/page'));
const AfternoonTeaPage = lazy(() => import('../pages/qualifications/afternoon-tea/page'));
const TeaSommelierPage = lazy(() => import('../pages/qualifications/tea-sommelier/page'));
const ButlerStyleUniformPage = lazy(() => import('../pages/qualifications/butler-style-uniform/page'));
const WineStewardPage = lazy(() => import('../pages/qualifications/wine-steward/page'));
const DiningCoordinatorPage = lazy(() => import('../pages/qualifications/dining-coordinator/page'));
const EventPlanningPage = lazy(() => import('../pages/qualifications/event-planning/page'));
const TravelConciergePage = lazy(() => import('../pages/qualifications/travel-concierge/page'));
const HouseholdManagerPage = lazy(() => import('../pages/qualifications/household-manager/page'));
const DistanceLearningPage = lazy(() => import('../pages/qualifications/distance-learning/page'));

const SeminarsPage = lazy(() => import('../pages/seminars/page'));
const TeamLeadershipPage = lazy(() => import('../pages/seminars/corporate/team-leadership/page'));
const ScheduleManagementPage = lazy(() => import('../pages/seminars/corporate/schedule-management/page'));
const CrossCulturalUnderstandingPage = lazy(() => import('../pages/seminars/corporate/cross-cultural-understanding/page'));
const HospitalityEnhancementPage = lazy(() => import('../pages/seminars/corporate/hospitality-enhancement/page'));
const CommunicationSkillsPage = lazy(() => import('../pages/seminars/corporate/communication-skills/page'));
const CrisisManagementPage = lazy(() => import('../pages/seminars/corporate/crisis-management/page'));

// News pages
const NewsPage = lazy(() => import('../pages/news/page'));
const AIHospitalityRevolutionPage = lazy(() => import('../pages/news/ai-hospitality-revolution/page'));
const AppleRobotButlerPage = lazy(() => import('../pages/news/apple-robot-butler/page'));
const ChinaButlerDemandSurgePage = lazy(() => import('../pages/news/china-butler-demand-surge/page'));
const DowntonAbbeyGrandFinalePage = lazy(() => import('../pages/news/downton-abbey-grand-finale/page'));
const GrantHarroldRoyalButlerMemoirPage = lazy(() => import('../pages/news/grant-harrold-royal-butler-memoir/page'));
const HongKongButlerAIProfitabilityPage = lazy(() => import('../pages/news/hong-kong-butler-ai-profitability/page'));
const JumeirahBurjAlArab25thAnniversaryPage = lazy(() => import('../pages/news/jumeirah-burj-al-arab-25th-anniversary/page'));
const RafflesButlerCampaignPage = lazy(() => import('../pages/news/raffles-butler-campaign/page'));
const RafflesLondonCampaignPage = lazy(() => import('../pages/news/raffles-london-campaign/page'));
const RafflesSentosaButlerPackagePage = lazy(() => import('../pages/news/raffles-sentosa-butler-package/page'));
const SwissLuxuryTravelSkiButlerPage = lazy(() => import('../pages/news/swiss-luxury-travel-ski-butler/page'));
const TibaGlobalAwardFiveYearsPage = lazy(() => import('../pages/news/tiba-global-award-five-years/page'));
const GlobalButlerShortageSalarySurgePage = lazy(() => import('../pages/news/global-butler-shortage-salary-surge/page'));
const WeaveRoboticsIsaacRobotButlerPage = lazy(() => import('../pages/news/weave-robotics-isaac-robot-butler/page'));
const GeneratedNewsArticlePage = lazy(() => import('../pages/news/ArticlePage'));

const MembershipPage = lazy(() => import('../pages/membership/page'));
const ContactPage = lazy(() => import('../pages/contact/page'));
const NotFoundPage = lazy(() => import('../pages/NotFound'));

// Related sites (関連サイト) - JA
const RelatedHubPage = lazy(() => import('../pages/related/page'));
const RelatedTheButlerPage = lazy(() => import('../pages/related/the-butler/page'));
const RelatedButlerBellPage = lazy(() => import('../pages/related/butler-bell/page'));
const RelatedButlerAcademyPage = lazy(() => import('../pages/related/butler-academy/page'));

const ButlerPage = lazy(() => import('../pages/butler/page'));
const ButlerBecomePage = lazy(() => import('../pages/butler/become/page'));

// Standards pages
const StandardsPage = lazy(() => import('../pages/standards/page'));
const StandardsEthicsPage = lazy(() => import('../pages/standards/ethics/page'));
const StandardsConductPage = lazy(() => import('../pages/standards/conduct/page'));
const StandardsHospitalityPage = lazy(() => import('../pages/standards/hospitality/page'));
const StandardsDeportmentPage = lazy(() => import('../pages/standards/deportment/page'));
const StandardsGroomingPage = lazy(() => import('../pages/standards/grooming/page'));
const StandardsConfidentialityPage = lazy(() => import('../pages/standards/confidentiality/page'));
const StandardsCrisisPage = lazy(() => import('../pages/standards/crisis/page'));
const StandardsInfosecPage = lazy(() => import('../pages/standards/infosec/page'));
const StandardsSchedulePage = lazy(() => import('../pages/standards/schedule/page'));
const StandardsAptitudePage = lazy(() => import('../pages/standards/aptitude/page'));
const StandardsCertificationPage = lazy(() => import('../pages/standards/certification/page'));
const StandardsAiRolePage = lazy(() => import('../pages/standards/ai-role/page'));

// English pages
const AboutPageEN = lazy(() => import('../pages/en/about/page'));
const AboutOverviewPageEN = lazy(() => import('../pages/en/about/overview/page'));
const AboutMissionPageEN = lazy(() => import('../pages/en/about/mission/page'));
const AboutHistoryPageEN = lazy(() => import('../pages/en/about/history/page'));
const AboutOrganizationPageEN = lazy(() => import('../pages/en/about/organization/page'));
const AboutActivitiesPageEN = lazy(() => import('../pages/en/about/activities/page'));
const AboutAccessPageEN = lazy(() => import('../pages/en/about/access/page'));
const QualificationsPageEN = lazy(() => import('../pages/en/qualifications/page'));
const AssociateButlerPageEN = lazy(() => import('../pages/en/qualifications/associate-butler/page'));
const ProfessionalButlerPageEN = lazy(() => import('../pages/en/qualifications/professional-butler/page'));
const MasterButlerPageEN = lazy(() => import('../pages/en/qualifications/master-butler/page'));
const MembershipPageEN = lazy(() => import('../pages/en/membership/page'));
const ButlerServicesPageEN = lazy(() => import('../pages/en/butler-services/page'));
const FootmanServicePageEN = lazy(() => import('../pages/en/services/footman/page'));
const ButlerServicePageEN = lazy(() => import('../pages/en/services/butler/page'));
const SecondStewardServicePageEN = lazy(() => import('../pages/en/services/second-steward/page'));
const HouseStewardServicePageEN = lazy(() => import('../pages/en/services/house-steward/page'));
const SeminarsPageEN = lazy(() => import('../pages/en/seminars/page'));
const TeamLeadershipPageEN = lazy(() => import('../pages/en/seminars/corporate/team-leadership/page'));
const HospitalityEnhancementPageEN = lazy(() => import('../pages/en/seminars/corporate/hospitality-enhancement/page'));
const CommunicationSkillsPageEN = lazy(() => import('../pages/en/seminars/corporate/communication-skills/page'));
const CrisisManagementPageEN = lazy(() => import('../pages/en/seminars/corporate/crisis-management/page'));
const CrossCulturalUnderstandingPageEN = lazy(() => import('../pages/en/seminars/corporate/cross-cultural-understanding/page'));
const ScheduleManagementPageEN = lazy(() => import('../pages/en/seminars/corporate/schedule-management/page'));
const NewsPageEN = lazy(() => import('../pages/en/news/page'));
const ContactPageEN = lazy(() => import('../pages/en/contact/page'));
const ButlerPageEN = lazy(() => import('../pages/en/butler/page'));
const ButlerBecomePageEN = lazy(() => import('../pages/en/butler/become/page'));

// English News Detail Pages
const EnNewsAiHospitalityRevolutionPage = lazy(() => import('../pages/en/news/ai-hospitality-revolution/page'));
const EnNewsDowntonAbbeyGrandFinalePage = lazy(() => import('../pages/en/news/downton-abbey-grand-finale/page'));
const EnNewsRafflesLondonCampaignPage = lazy(() => import('../pages/en/news/raffles-london-campaign/page'));
const EnNewsAppleRobotButlerPage = lazy(() => import('../pages/en/news/apple-robot-butler/page'));
const EnNewsChinaButlerDemandSurgePage = lazy(() => import('../pages/en/news/china-butler-demand-surge/page'));
const EnNewsGlobalButlerShortageSalarySurgePage = lazy(() => import('../pages/en/news/global-butler-shortage-salary-surge/page'));
const EnNewsGrantHarroldRoyalButlerMemoirPage = lazy(() => import('../pages/en/news/grant-harrold-royal-butler-memoir/page'));
const EnNewsHongKongButlerAiProfitabilityPage = lazy(() => import('../pages/en/news/hong-kong-butler-ai-profitability/page'));
const EnNewsJumeirahBurjAlArab25thAnniversaryPage = lazy(() => import('../pages/en/news/jumeirah-burj-al-arab-25th-anniversary/page'));
const EnNewsRafflesButlerCampaignPage = lazy(() => import('../pages/en/news/raffles-butler-campaign/page'));
const EnNewsRafflesSentosaButlerPackagePage = lazy(() => import('../pages/en/news/raffles-sentosa-butler-package/page'));
const EnNewsSwissLuxuryTravelSkiButlerPage = lazy(() => import('../pages/en/news/swiss-luxury-travel-ski-butler/page'));
const EnNewsTibaGlobalAwardFiveYearsPage = lazy(() => import('../pages/en/news/tiba-global-award-five-years/page'));
const EnNewsWeaveRoboticsIsaacRobotButlerPage = lazy(() => import('../pages/en/news/weave-robotics-isaac-robot-butler/page'));

// Related sites (関連サイト) - EN
const EnRelatedHubPage = lazy(() => import('../pages/en/related/page'));
const EnRelatedTheButlerPage = lazy(() => import('../pages/en/related/the-butler/page'));
const EnRelatedButlerBellPage = lazy(() => import('../pages/en/related/butler-bell/page'));
const EnRelatedButlerAcademyPage = lazy(() => import('../pages/en/related/butler-academy/page'));

const BookPage = lazy(() => import('../pages/book/page'));
const BookPageEN = lazy(() => import('../pages/en/book/page'));

const routes: RouteObject[] = [
  { path: '/book/butler-manners-and-culture', element: <BookPage /> },
  { path: '/en/book/butler-manners-and-culture/', element: <BookPageEN /> },
  {
    path: '/',
    element: <HomePage />
  },
  {
    path: '/en/',
    element: <HomePageEN />
  },
  {
    path: '/about',
    element: <AboutPage />
  },
  {
    path: '/about/overview',
    element: <AboutOverviewPage />
  },
  {
    path: '/about/mission',
    element: <AboutMissionPage />
  },
  {
    path: '/about/history',
    element: <AboutHistoryPage />
  },
  {
    path: '/about/organization',
    element: <AboutOrganizationPage />
  },
  {
    path: '/about/activities',
    element: <AboutActivitiesPage />
  },
  {
    path: '/about/access',
    element: <AboutAccessPage />
  },

  // Services
  {
    path: '/butler-services/',
    element: <ButlerServicesPage />
  },
  {
    path: '/services/footman/',
    element: <FootmanServicePage />
  },
  {
    path: '/services/butler/',
    element: <ButlerServicePage />
  },
  {
    path: '/services/second-steward/',
    element: <SecondStewardServicePage />
  },
  {
    path: '/services/house-steward/',
    element: <HouseStewardServicePage />
  },

  // Qualifications routes
  {
    path: '/qualifications',
    element: <QualificationsPage />
  },
  {
    path: '/qualifications/associate-butler',
    element: <AssociateButlerPage />
  },
  {
    path: '/qualifications/professional-butler',
    element: <ProfessionalButlerPage />
  },
  {
    path: '/qualifications/master-butler',
    element: <MasterButlerPage />
  },
  {
    path: '/qualifications/business-manner',
    element: <BusinessMannerPage />
  },
  {
    path: '/qualifications/afternoon-tea',
    element: <AfternoonTeaPage />
  },
  {
    path: '/qualifications/tea-sommelier',
    element: <TeaSommelierPage />
  },
  {
    path: '/qualifications/butler-style-uniform',
    element: <ButlerStyleUniformPage />
  },
  {
    path: '/qualifications/wine-steward',
    element: <WineStewardPage />
  },
  {
    path: '/qualifications/dining-coordinator',
    element: <DiningCoordinatorPage />
  },
  {
    path: '/qualifications/event-planning',
    element: <EventPlanningPage />
  },
  {
    path: '/qualifications/travel-concierge',
    element: <TravelConciergePage />
  },
  {
    path: '/qualifications/household-manager',
    element: <HouseholdManagerPage />
  },
  {
    path: '/qualifications/distance-learning',
    element: <DistanceLearningPage />
  },

  {
    path: '/seminars',
    element: <SeminarsPage />
  },
  {
    path: '/seminars/corporate/team-leadership',
    element: <TeamLeadershipPage />
  },
  {
    path: '/seminars/corporate/schedule-management',
    element: <ScheduleManagementPage />
  },
  {
    path: '/seminars/corporate/cross-cultural-understanding',
    element: <CrossCulturalUnderstandingPage />
  },
  {
    path: '/seminars/corporate/hospitality-enhancement',
    element: <HospitalityEnhancementPage />
  },
  {
    path: '/seminars/corporate/communication-skills',
    element: <CommunicationSkillsPage />
  },
  {
    path: '/seminars/corporate/crisis-management',
    element: <CrisisManagementPage />
  },

  // News routes
  {
    path: '/news',
    element: <NewsPage />
  },
  {
    path: '/news/ai-hospitality-revolution',
    element: <AIHospitalityRevolutionPage />
  },
  {
    path: '/news/apple-robot-butler',
    element: <AppleRobotButlerPage />
  },
  {
    path: '/news/china-butler-demand-surge',
    element: <ChinaButlerDemandSurgePage />
  },
  {
    path: '/news/downton-abbey-grand-finale',
    element: <DowntonAbbeyGrandFinalePage />
  },
  {
    path: '/news/grant-harrold-royal-butler-memoir',
    element: <GrantHarroldRoyalButlerMemoirPage />
  },
  {
    path: '/news/hong-kong-butler-ai-profitability',
    element: <HongKongButlerAIProfitabilityPage />
  },
  {
    path: '/news/jumeirah-burj-al-arab-25th-anniversary',
    element: <JumeirahBurjAlArab25thAnniversaryPage />
  },
  {
    path: '/news/raffles-butler-campaign',
    element: <RafflesButlerCampaignPage />
  },
  {
    path: '/news/raffles-london-campaign',
    element: <RafflesLondonCampaignPage />
  },
  {
    path: '/news/raffles-sentosa-butler-package',
    element: <RafflesSentosaButlerPackagePage />
  },
  {
    path: '/news/swiss-luxury-travel-ski-butler',
    element: <SwissLuxuryTravelSkiButlerPage />
  },
  {
    path: '/news/tiba-global-award-five-years',
    element: <TibaGlobalAwardFiveYearsPage />
  },
  {
    path: '/news/global-butler-shortage-salary-surge',
    element: <GlobalButlerShortageSalarySurgePage />
  },
  {
    path: '/news/weave-robotics-isaac-robot-butler',
    element: <WeaveRoboticsIsaacRobotButlerPage />
  },
  { path: '/news/:slug', element: <GeneratedNewsArticlePage /> },

  {
    path: '/membership',
    element: <MembershipPage />
  },
  {
    path: '/contact',
    element: <ContactPage />
  },

  // Related sites routes (JA)
  {
    path: '/related',
    element: <RelatedHubPage />
  },
  {
    path: '/related/the-butler',
    element: <RelatedTheButlerPage />
  },
  {
    path: '/related/butler-bell',
    element: <RelatedButlerBellPage />
  },
  {
    path: '/related/butler-academy',
    element: <RelatedButlerAcademyPage />
  },

  {
    path: '/butler',
    element: <ButlerPage />
  },
  {
    path: '/butler/become',
    element: <ButlerBecomePage />
  },

  // Standards routes
  {
    path: '/standards',
    element: <StandardsPage />
  },
  {
    path: '/standards/ethics',
    element: <StandardsEthicsPage />
  },
  {
    path: '/standards/conduct',
    element: <StandardsConductPage />
  },
  {
    path: '/standards/hospitality',
    element: <StandardsHospitalityPage />
  },
  {
    path: '/standards/deportment',
    element: <StandardsDeportmentPage />
  },
  {
    path: '/standards/grooming',
    element: <StandardsGroomingPage />
  },
  {
    path: '/standards/confidentiality',
    element: <StandardsConfidentialityPage />
  },
  {
    path: '/standards/crisis',
    element: <StandardsCrisisPage />
  },
  {
    path: '/standards/infosec',
    element: <StandardsInfosecPage />
  },
  {
    path: '/standards/schedule',
    element: <StandardsSchedulePage />
  },
  {
    path: '/standards/aptitude',
    element: <StandardsAptitudePage />
  },
  {
    path: '/standards/certification',
    element: <StandardsCertificationPage />
  },
  {
    path: '/standards/ai-role',
    element: <StandardsAiRolePage />
  },

  // English routes
  {
    path: '/en/about/',
    element: <AboutPageEN />
  },
  {
    path: '/en/about/overview/',
    element: <AboutOverviewPageEN />
  },
  {
    path: '/en/about/mission/',
    element: <AboutMissionPageEN />
  },
  {
    path: '/en/about/history/',
    element: <AboutHistoryPageEN />
  },
  {
    path: '/en/about/organization/',
    element: <AboutOrganizationPageEN />
  },
  {
    path: '/en/about/activities/',
    element: <AboutActivitiesPageEN />
  },
  {
    path: '/en/about/access/',
    element: <AboutAccessPageEN />
  },
  {
    path: '/en/qualifications/',
    element: <QualificationsPageEN />
  },
  {
    path: '/en/qualifications/associate-butler/',
    element: <AssociateButlerPageEN />
  },
  {
    path: '/en/qualifications/professional-butler/',
    element: <ProfessionalButlerPageEN />
  },
  {
    path: '/en/qualifications/master-butler/',
    element: <MasterButlerPageEN />
  },
  {
    path: '/en/membership/',
    element: <MembershipPageEN />
  },
  {
    path: '/en/butler-services/',
    element: <ButlerServicesPageEN />
  },
  {
    path: '/en/services/footman/',
    element: <FootmanServicePageEN />
  },
  {
    path: '/en/services/butler/',
    element: <ButlerServicePageEN />
  },
  {
    path: '/en/services/second-steward/',
    element: <SecondStewardServicePageEN />
  },
  {
    path: '/en/services/house-steward/',
    element: <HouseStewardServicePageEN />
  },
  {
    path: '/en/seminars/',
    element: <SeminarsPageEN />
  },
  {
    path: '/en/seminars/corporate/team-leadership/',
    element: <TeamLeadershipPageEN />
  },
  {
    path: '/en/seminars/corporate/hospitality-enhancement/',
    element: <HospitalityEnhancementPageEN />
  },
  {
    path: '/en/seminars/corporate/communication-skills/',
    element: <CommunicationSkillsPageEN />
  },
  {
    path: '/en/seminars/corporate/crisis-management/',
    element: <CrisisManagementPageEN />
  },
  {
    path: '/en/seminars/corporate/cross-cultural-understanding/',
    element: <CrossCulturalUnderstandingPageEN />
  },
  {
    path: '/en/seminars/corporate/schedule-management/',
    element: <ScheduleManagementPageEN />
  },
  {
    path: '/en/news/',
    element: <NewsPageEN />
  },
  {
    path: '/en/contact/',
    element: <ContactPageEN />
  },
  {
    path: '/en/butler/',
    element: <ButlerPageEN />
  },
  {
    path: '/en/butler/become/',
    element: <ButlerBecomePageEN />
  },

  // Related sites routes (EN)
  {
    path: '/en/related/',
    element: <EnRelatedHubPage />
  },
  {
    path: '/en/related/the-butler/',
    element: <EnRelatedTheButlerPage />
  },
  {
    path: '/en/related/butler-bell/',
    element: <EnRelatedButlerBellPage />
  },
  {
    path: '/en/related/butler-academy/',
    element: <EnRelatedButlerAcademyPage />
  },

  // English News Detail Pages
  {
    path: '/en/news/ai-hospitality-revolution',
    element: <EnNewsAiHospitalityRevolutionPage />
  },
  {
    path: '/en/news/downton-abbey-grand-finale',
    element: <EnNewsDowntonAbbeyGrandFinalePage />
  },
  {
    path: '/en/news/raffles-london-campaign',
    element: <EnNewsRafflesLondonCampaignPage />
  },
  {
    path: '/en/news/apple-robot-butler',
    element: <EnNewsAppleRobotButlerPage />
  },
  {
    path: '/en/news/china-butler-demand-surge',
    element: <EnNewsChinaButlerDemandSurgePage />
  },
  {
    path: '/en/news/global-butler-shortage-salary-surge',
    element: <EnNewsGlobalButlerShortageSalarySurgePage />
  },
  {
    path: '/en/news/grant-harrold-royal-butler-memoir',
    element: <EnNewsGrantHarroldRoyalButlerMemoirPage />
  },
  {
    path: '/en/news/hong-kong-butler-ai-profitability',
    element: <EnNewsHongKongButlerAiProfitabilityPage />
  },
  {
    path: '/en/news/jumeirah-burj-al-arab-25th-anniversary',
    element: <EnNewsJumeirahBurjAlArab25thAnniversaryPage />
  },
  {
    path: '/en/news/raffles-butler-campaign',
    element: <EnNewsRafflesButlerCampaignPage />
  },
  {
    path: '/en/news/raffles-sentosa-butler-package',
    element: <EnNewsRafflesSentosaButlerPackagePage />
  },
  {
    path: '/en/news/swiss-luxury-travel-ski-butler',
    element: <EnNewsSwissLuxuryTravelSkiButlerPage />
  },
  {
    path: '/en/news/tiba-global-award-five-years',
    element: <EnNewsTibaGlobalAwardFiveYearsPage />
  },
  {
    path: '/en/news/weave-robotics-isaac-robot-butler',
    element: <EnNewsWeaveRoboticsIsaacRobotButlerPage />
  },
  { path: '/en/news/:slug', element: <GeneratedNewsArticlePage /> },

  {
    path: '*',
    element: <NotFoundPage />
  }
];

export default routes;
